import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useToast } from '@/components/ui';
import {
    approveApp,
    checkAppConnection,
    normalizeApiErrorDetails,
    type ConnectionCheckReport,
    type RegistryApp,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { appConnectionFingerprint, isApprovalUnlocked, type ApprovalCheck } from './approvalGate';

export interface AppApprovalOptions {
    /** `console.apps.credentials` — what the server requires for the connection check. */
    canCheck: boolean;
    /** `console.apps.manage` — what the server requires for `/approve`. */
    canApprove: boolean;
    /** Called after a successful approval, once caches are invalidated. */
    onApproved?: () => void;
}

function errorDetails(error: unknown, key: string): string[] {
    if (!(error instanceof OrgAdminApiError)) return [];
    return normalizeApiErrorDetails(error.details)[key] ?? [];
}

/**
 * EAA-1709: check → approve. "Duyệt" is offered ONLY after a connection check in
 * this session returned no `failed` probe for the app's CURRENT connection
 * config — the same rule `POST …/approve` enforces server-side, so the button is
 * never offered for a call the server would refuse. A later failing check, an
 * edit to the checked fields (see `appConnectionFingerprint`) or `invalidate()`
 * hides it again. The report lives in component state only; nothing is cached.
 */
export function useAppApproval(app: RegistryApp | null | undefined, options: AppApprovalOptions) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const [check, setCheck] = useState<ApprovalCheck | null>(null);

    const checkMutation = useMutation({
        mutationFn: async (target: RegistryApp) => ({
            target,
            report: await checkAppConnection(config.apiBase, accessToken as string, target.code),
        }),
        onSuccess: ({ target, report }) =>
            setCheck({ code: target.code, fingerprint: appConnectionFingerprint(target), report }),
        onError: (error) => {
            const retryAfter = errorDetails(error, 'retry_after_seconds')[0];
            toast.error(
                retryAfter
                    ? t('console.appDetail.connection.check.cooldownError', { seconds: retryAfter })
                    : t('console.appDetail.connection.check.error')
            );
        },
    });

    const approveMutation = useMutation({
        mutationFn: (target: RegistryApp) =>
            approveApp(config.apiBase, accessToken as string, target.code),
        onSuccess: (_data, target) => {
            setCheck(null);
            toast.success(t('console.appDetail.approval.approveSuccess', { name: target.name }));
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            void queryClient.invalidateQueries({ queryKey: ['apps-registry'] });
            void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
            void queryClient.invalidateQueries({ queryKey: ['app-checklist', target.code] });
            options.onApproved?.();
        },
        onError: (error) => {
            // The server re-ran the check and a probe failed since ours: the gate
            // closes, and its own list of failing probes is the explanation.
            const refused = errorDetails(error, 'connection_check');
            if (refused.length > 0) {
                setCheck(null);
                toast.error(
                    t('console.appDetail.approval.approveRefused', { details: refused.join('; ') })
                );
                return;
            }
            toast.error(t('console.appDetail.approval.approveError'));
        },
    });

    const currentCheck = check && app && check.code === app.code ? check : null;
    const report: ConnectionCheckReport | null = currentCheck?.report ?? null;
    const stale = Boolean(
        currentCheck && app && currentCheck.fingerprint !== appConnectionFingerprint(app)
    );

    const runCheck = useCallback(() => {
        if (app) checkMutation.mutate(app);
    }, [app, checkMutation]);

    const approve = useCallback(() => {
        if (app) approveMutation.mutate(app);
    }, [app, approveMutation]);

    const invalidate = useCallback(() => setCheck(null), []);

    return {
        canRunCheck: options.canCheck && Boolean(app),
        runCheck,
        checking: checkMutation.isPending,
        report,
        stale,
        canApprove: options.canApprove && isApprovalUnlocked(currentCheck, app),
        approve,
        approving: approveMutation.isPending,
        invalidate,
    };
}

export type AppApprovalVM = ReturnType<typeof useAppApproval>;
